import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { ChatUIProps, ToolPlugin, ChatRef } from "@hose/chat-app";
import { formTools } from "../plugins/formTools";
import { ChatHeader, Welcome } from "../components";
import { ConsumptionCard } from '../components/MyConsumptions/ConsumptionCard'
import { useChatInitialization, useConfig, useIsEmbedded, useReimburseTrigger } from "../hooks/useChatInitialization";
import * as Icons from '@hose/eui-icons'
import { sendStartNewChat, sendPreviewFile, sendSelectInvoice, createSafeFetcher } from '../utils/postmate'
import { Button, Dropdown, MenuProps } from '@hose/eui'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw';
import { v4 as uuid } from 'uuid';
import "../App.less";
import { getParamFromUrl, formatAmount } from "../utils/tools";
import { invoiceMap } from "../utils/invoice-utils";
import Logger from "../utils/Logger";

const maxSize = 10  // 单位是 M
const maxCount = 10

/**
 * 合并所有工具插件，包括欢迎页面工具
 */
const tools: ToolPlugin[] = [
  ...formTools // 填写单据 tool
];

interface HomeProps {
  fetcher: typeof fetch;
  logger?: Logger
}

function Home({ fetcher, logger }: HomeProps) {
  // 检测是否在 iframe 中
  const isEmbedded = useIsEmbedded();
  const chatRef = useRef<ChatRef>(null)

  // 动态加载的 createChatAgent 函数
  const [createChatAgent, setCreateChatAgent] = useState<any>(null);
  const [isChatAgentLoaded, setIsChatAgentLoaded] = useState(false);

  // 获取聊天初始化配置，包括动态sessionId和specificationId
  const { sessionId, specificationId, isInitialized, flowCode, flowId, auditFlowCharge, page } = useChatInitialization();

  const { invoicePowers = [] } = useConfig();
  const reimbursePayload = useReimburseTrigger();

  // 创建一个安全的 fetch 包装器，确保上下文正确
  const safeFetcher = useMemo(() => createSafeFetcher(fetcher), [fetcher]);

  // 父应用通过 Postmate 触发的报销请求
  useEffect(() => {
    if (!isChatAgentLoaded || !reimbursePayload || reimbursePayload.length === 0) return;
    chatRef?.current && chatRef.current.sendMsg('帮我报销', [], reimbursePayload);
  }, [isChatAgentLoaded, reimbursePayload]);

  // 动态加载 createChatAgent 函数
  useEffect(() => {
    async function loadChatAgent() {
      const platform = getParamFromUrl('platform'); // "mobile" 或 null

      let mod: any;
      if (platform === 'mobile') {
        mod = await import('@hose/chat-app/mobile');
      } else {
        mod = await import('@hose/chat-app');
      }

      setCreateChatAgent(() => mod.createChatAgent);
      setIsChatAgentLoaded(true);
    }

    loadChatAgent();
  }, []);


  const { Chat } = useMemo(
    () => {
      if (!createChatAgent) {
        return { Chat: null };
      }
      return createChatAgent({
        tools,
        fetch: safeFetcher,
        logger,
        assistMode: 'auto',
      })
    },
    [createChatAgent, safeFetcher, logger]
  );

  const promptItems = useMemo(() => {
    const items = [
      {
        icon: <Icons.TwoToneGeneralAutofill fontSize={20} />,
        label: '上传发票或附件，帮您提取信息快速填单',
        description: '快速填写单据',
        key: "form",
      }
    ]
    if (auditFlowCharge) {
      items.push({
        icon: <Icons.TwoToneGeneralAiApproval1 fontSize={20} />,
        label: '帮您提前发现问题，告别反复修改',
        description: '智能预审单据',
        key: "audit_flow",
      })
    }
    return items
  }, [auditFlowCharge])

  const showModeSwitch = useMemo(() => {
    const serverHost = getParamFromUrl('serverHost')
    return serverHost?.includes('mentor')
  }, [])

  const attachmentPreview: ChatUIProps['attachmentPreview'] = (info) => {
    return <div onClick={(e) => {
      e.stopPropagation()
      sendPreviewFile(info)
    }} style={{ width: '100%', height: '100%' }} />
  }

  const onQuickForm = () => {
    chatRef?.current && chatRef.current.sendMsg('帮我快速填写单据');
  }

  const onAuditFlow = () => {
    chatRef?.current && chatRef.current.sendMsg('帮我智能预审单据');
  }

  const items: MenuProps['items'] = useMemo(() => {
    const invoiceMenuMap = {
      ocr: {
        key: 'ocr',
        label: '智能识票',
        icon: <Icons.FilledGeneralCamera fontSize={16} color="var(--eui-function-warning-500)" />,
        action: 'onImportOCRClick'
      },
      pdfInvoice: {
        key: 'pdfInvoice',
        label: '电子发票文件',
        icon: <Icons.TwoToneDataPdf fontSize={16} color="var(--eui-function-danger-500)" />,
        action: 'onImportInvoiceClick'
      },
      inputInvoice: {
        key: 'inputInvoice',
        label: '手录发票',
        icon: <Icons.FilledEditEdit fontSize={16} color="var(--eui-primary-pri-500)" />,
        action: 'onImportInputInvoiceClick'
      },
      aliPayInvoice: {
        key: 'aliPayInvoice',
        label: '支付宝发票',
        icon: <Icons.FilledLogoAlipay fontSize={16} color="var(--eui-function-info-500)" />,
        action: 'onImportAliPayInvoiceClick'
      },
      medical: {
        key: 'medical',
        label: '医疗发票',
        icon: <Icons.FilledGeneralMedical fontSize={16} color="var(--eui-decorative-cyan-600)" />,
        action: 'onImportOCRMedicalClick'
      },
      aifapiao: {
        key: 'aifapiao',
        label: '爱发票',
        icon: <Icons.TwoToneLogoAifapiao fontSize={16} />,
        action: 'onImportAifaPiaoInvoiceClick'
      },
      formPoolSelect: {
        key: 'formPoolSelect',
        label: '票池选择',
        icon: <Icons.FilledGeneralFolder fontSize={16} color="var(--eui-function-success-500)" />,
        action: 'onSelectInvoiceFromPool'
      },
      overseasInvoice: {
        key: 'overseasInvoice',
        label: '海外票据识别',
        icon: <Icons.OutlinedOtherOverseasBills fontSize={16} color="var(--eui-decorative-pur-500)" />,
        action: 'onImportOverseasInvoiceClick'
      }
    }
    const invoiceMenu = invoicePowers
      .map(key => invoiceMenuMap[key as keyof typeof invoiceMenuMap])
      .filter(Boolean) as NonNullable<MenuProps['items']>;
    return [
      {
        label: '附件',
        type: 'group' as const,
        children: [
          {
            label: '上传附件',
            key: 'attachment',
            icon: <Icons.OutlinedDirectionUpload fontSize={16} color="var(--eui-primary-pri-500)" />,
            action: 'onImportOCRClick'
          }
        ]
      },
      {
        type: 'divider' as const,
      },
      {
        label: '发票识别',
        type: 'group' as const,
        children: invoiceMenu
      }
    ]
  }, [invoicePowers])

  const handleMenuClick: MenuProps['onClick'] = useCallback(async (e: any) => {
    if (e.key === 'attachment') {
      chatRef?.current && chatRef.current.upload();
      return;
    }
    const res = await sendSelectInvoice({
      type: e.key
    })
    addAttachItems(res)
  }, [chatRef]);

  const addAttachItems = (res: any) => {
    if (res?.length > 0) {
      const invoices = res?.map((item: any) => {
        const invoiceItem = getParamFromUrl('platform') === 'mobile' ? {
          ...item,
          master: item?.invoiceId,
          details: item?.itemsIds,
        } : item
        const suffix = item.fileId?.key?.split('.').pop() || 'pdf'
        const fileUrl = item?.fileId?.url
        const invoiceInfo = invoiceMap[invoiceItem?.master?.entityId](invoiceItem)
        return {
          uid: uuid(),
          status: 'done',
          mimeType: 'invoice',
          invoiceId: invoiceItem?.master?.id,
          name: `${invoiceInfo?.title}.${suffix}`,
          merchantName: invoiceInfo?.title,
          description: `${invoiceInfo?.date} ${formatAmount(invoiceInfo?.sourceAmount)}`,
          date: invoiceInfo?.date,
          amount: formatAmount(invoiceInfo?.sourceAmount),
          url: fileUrl
        }
      })
      chatRef?.current && chatRef.current.addAttachItems(invoices)
    }
  }

  const toolsArea = useMemo(() => {
    if (invoicePowers.length > 0) {
      if (getParamFromUrl('platform') === 'mobile') {
        return [
          <Button
            className="upload-tools"
            size="small"
            category="text"
            onClick={async () => {
              const res = await sendSelectInvoice({})
              if (res.type === 'attachment') {
                chatRef?.current && chatRef.current.upload();
                return;
              }
              addAttachItems(res.data)
            }}
            icon={
              <Icons.OutlinedTipsMoreAdd
                fontSize={22}
                color="var(--eui-icon-n1, rgba(29, 33, 41, 0.80))"
              />}
            style={{ order: 1 }}
          />
        ]
      }
      return [
        <Dropdown menu={{ items, onClick: handleMenuClick }} placement="topRight" overlayStyle={{ minWidth: '210px' }}>
          <Button
            size="small"
            category="text"
            className="upload-tools"
            icon={
              <Icons.OutlinedGeneralAttachment
                fontSize={20}
              />}
            style={{ order: 1 }} />
        </Dropdown>
      ]
    }
    return undefined

  }, [items, handleMenuClick, invoicePowers])

  const bubbleCustomPartRender = (part: any) => {
    console.log(part, 'look')
    // 只处理业务类型，内置类型自动处理
    switch (part.type) {
      case 'consumption_detail':
        return <ConsumptionCard
          {...part}
          title={`${part.consumptionCategory}${part.consumptionType ? ` - ${part.consumptionType}` : ''}`}
          amountText={formatAmount(part?.consumptionAmount)}
        />;
      default:
        return null;
    }
  }

  const contextValue = useMemo(() => {
    if (page === 'MyConsumptions') {
      return {
        placeholder: '请描述您的消费，例如：昨天下午招待客户吃饭，580元',
        content: '帮我记录消费'
      }
    }
    return {
      placeholder: "输入您的问题或想法",
      content: "帮我填写单据"
    }
  }, [page])

  // 等待初始化完成和 ChatAgent 加载完成
  if (!isInitialized || !isChatAgentLoaded || !Chat) {
    return <div className="chat-loading">正在初始化聊天...</div>;
  }

  return (
    <>
      {!isEmbedded && <ChatHeader className="chat-header" />}
      {/* // 防止下面按钮中的两个彩色按钮不显示，很奇怪的，提前加载一个就不会不显示 */}
      <Icons.TwoToneGeneralAutofill fontSize={0} />
      <Icons.TwoToneGeneralAiApproval1 fontSize={0} />
      {/* 临时添加调试组件 - 仅在开发环境显示 */}
      {/* {isEmbedded && process.env.NODE_ENV === 'development' && <PostmateDebug />} */}
      <Chat
        ref={chatRef}
        placeholder={contextValue.placeholder}
        bubbleMdPlugins={[remarkGfm]}
        bubbleMdRehypePlugins={[rehypeRaw]}
        sessionId={sessionId}
        className="chat-ui-container"
        attachmentMaxCount={maxCount}
        attachmentMaxSize={maxSize}
        welcomeComp={<Welcome />}
        promptTitle="我可以帮您："
        promptCls="custom-prompt"
        inputCls="custom-input"
        toolsArea={toolsArea}
        content={contextValue.content}
        promptItems={promptItems}
        bubbleAction={() => {
          return null
        }}
        bubbleAvatar={{
          user: <div className="user-avatar" />,
          assistant: <Icons.TwoToneLogoAi className="assistant-avatar" fontSize={26} />,
        }}
        bubbleCls="custom-bubble"
        disableInput={false}
        disableSpeech={true}
        attachmentPreview={attachmentPreview}
        onSendMsg={(ctx: any) => {
          // 使用从iframe初始化数据中获取的specificationId
          const addonAnnotationParams: Record<string, string> = {}
          if (specificationId && specificationId !== 'null') {
            addonAnnotationParams.specificationId = specificationId
          }
          if (flowCode && flowCode !== 'null') {
            addonAnnotationParams.flowCode = flowCode
          }
          if (flowId && flowId !== 'null') {
            addonAnnotationParams.flowId = flowId
          }
          if (Object.keys(addonAnnotationParams)?.length > 0) {
            ctx.addonAnnotation(addonAnnotationParams);
          }
        }}
        quickArea={
          <div className="quick-area">
            <Button category="secondary" onClick={sendStartNewChat}>
              <Icons.OutlinedTipsMoreAdd fontSize={16} />
              <span className="text">新对话</span>
            </Button>
            {/* // 彩色按钮放在 icon里在 ios 上发送消息后会不显示，很奇怪的，放外边就不会不显示 */}
            <Button category="secondary" onClick={onQuickForm}>
              <Icons.TwoToneGeneralAutofill fontSize={0} />
              <Icons.TwoToneGeneralAutofill fontSize={16} />
              <span className="text">快速填单</span>
            </Button>
            {auditFlowCharge && <Button category="secondary" onClick={onAuditFlow}>
              <Icons.TwoToneGeneralAiApproval1 fontSize={0} />
              <Icons.TwoToneGeneralAiApproval1 fontSize={16} />
              <span className="text">智能预审</span>
            </Button>}
          </div>
        }
        showModeSwitch={showModeSwitch}
        bubbleCustomPartRender={bubbleCustomPartRender}
      />
    </>
  );
}

export default Home;
